import React, { useEffect, useRef, useState } from 'react';
import { Form as FinalForm } from 'react-final-form';
import {
  Button,
  FieldTextInput,
  FieldSelect,
  FieldCurrencyInput,
  IconCollection,
  IconSpinner,
} from '../../components';
import { FormattedMessage } from 'react-intl';
import { types as sdkTypes } from '../../util/sdkLoader';
import { formatMoney } from '../../util/currency';

import css from './BillReductionModal.module.css';

const { Money } = sdkTypes;

const toComparableDate = dateStr => {
  if (!dateStr || typeof dateStr !== 'string') return null;

  // Common incoming format: 'Feb 21,2026' -> make it 'Feb 21, 2026'
  const normalized = dateStr.replace(/,(\d{4})$/, ', $1');
  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? null : d;
};

const getLatestEventDate = eventDates => {
  if (!Array.isArray(eventDates) || eventDates.length === 0) return null;

  return eventDates
    .map(toComparableDate)
    .filter(Boolean)
    .reduce((latest, d) => (!latest || d.getTime() > latest.getTime() ? d : latest), null);
};

const uploadSingleFileToCloudinary = async file => {
  const cloudName =
    process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || process.env.REACT_APP_CLOUDINARY_NAME;
  const uploadPreset =
    process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || process.env.REACT_APP_CLOUDINARY_PRESET;

  if (!cloudName || !uploadPreset) {
    throw new Error('Cloudinary env vars are missing (cloud name / upload preset).');
  }

  // Detect file type (based on the existing logic in EditListingPage.duck.js)
  let resourceType = 'image';
  const fileType = (file?.type || '').toLowerCase();

  if (fileType.startsWith('image')) {
    resourceType = 'image';
  } else if (fileType.startsWith('video')) {
    resourceType = 'video';
  } else if (fileType.startsWith('application')) {
    // PDF/DOC etc. can be uploaded via the image endpoint with unsigned presets
    resourceType = 'image';
  }

  const formData = new FormData();
  formData.append('file', file);
  formData.append('folder', 'overtimeBillDocuments');
  formData.append('resource_type', resourceType);
  formData.append('upload_preset', uploadPreset);

  const res = await window.fetch(`https://api.cloudinary.com/v1_1/${cloudName}/${resourceType}/upload`, {
    method: 'POST',
    body: formData,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => null);
    const message = err?.error?.message || 'File upload failed.';
    throw new Error(message);
  }

  const d = await res.json();
  return {
    secure_url: d.secure_url,
    public_id: d.public_id,
    resource_type: d.resource_type,
    original_filename: d.original_filename,
    bytes: d.bytes,
  };
};


const CreateInvoiceProviderModal = props => {
  const { 
    intl, 
    onSubmit, 
    onClose, 
    isLoading, 
    transactionsData,
    config,
  } = props;

  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [uploadError, setUploadError] = useState(null);
  const [uploadInProgress, setUploadInProgress] = useState(false);
  const [expenses, setExpenses] = useState([]);
  // console.log('expenses', expenses);
  const fileInputRef = useRef(null);
  const selectedDocumentsRef = useRef([]);

  const txItems = Array.isArray(transactionsData?.transactions) ? transactionsData.transactions : [];

  const handleAddExpense = (values, form) => {
    if (expenses.length >= 3) return;

    const draft = values?.expenseDraft || {};

    const category = (draft?.category || '').trim();
    const quantity = draft?.quantity;
    const cost = {
        amount: draft?.cost?.amount,
        currency: draft?.cost?.currency
    };

    const hasQty = quantity !== undefined && quantity !== null && quantity !== '';
    const hasCost = Object.keys(cost).length > 0 && Object.hasOwn(cost, 'amount') && Object.hasOwn(cost, 'currency');

    if (!category || !hasQty || !hasCost) return;

    const nextExpense = {
      id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
      category,
      quantity,
      cost,
    };

    setExpenses(prev => [...prev, nextExpense].slice(0, 3));
    if (form && typeof form.change === 'function') {
      form.change('expenseDraft.category', undefined);
      form.change('expenseDraft.quantity', undefined);
      form.change('expenseDraft.cost', undefined);
    }
  };

  const handleRemoveExpense = (id) => {
    setExpenses(expenses.filter(e => e.id !== id));
  };

  const handleRemoveDocument = docId => {
    setSelectedDocuments(prev => {
      const arr = Array.isArray(prev) ? prev : [];
      const removed = arr.find(d => d && d.id === docId);
      if (removed && removed.previewUrl) {
        try {
          URL.revokeObjectURL(removed.previewUrl);
        } catch (e) {
          // no-op
        }
      }
      return arr.filter(doc => doc.id !== docId);
    });
  };

  const handlePickDocument = () => {
    if (uploadInProgress) return;
    if (selectedDocuments.length >= 5) return;
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleDocumentSelected = e => {
    try {
      setUploadError(null);

      const file = e?.target?.files?.[0];
      if (!file) return;

      const currentCount = Array.isArray(selectedDocuments) ? selectedDocuments.length : 0;
      if (currentCount >= 5) return;

      const isImage = typeof file.type === 'string' && file.type.startsWith('image/');
      const previewUrl = isImage ? URL.createObjectURL(file) : null;

      const nextDoc = {
        id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
        file,
        fileName: file.name,
        bytes: file.size,
        type: file.type,
        previewUrl,
        selectedAt: new Date().toISOString(),
      };

      setSelectedDocuments(prev => {
        const arr = Array.isArray(prev) ? prev : [];
        return [...arr, nextDoc].slice(0, 5);
      });
    } catch (err) {
      setUploadError(err?.message || 'File selection failed.');
    } finally {
      if (e?.target) {
        e.target.value = '';
      }
    }
  };

  useEffect(() => {
    selectedDocumentsRef.current = Array.isArray(selectedDocuments) ? selectedDocuments : [];
  }, [selectedDocuments]);

  useEffect(() => {
    return () => {
      const arr = Array.isArray(selectedDocumentsRef.current) ? selectedDocumentsRef.current : [];
      arr.forEach(d => {
        if (d && d.previewUrl) {
          try {
            URL.revokeObjectURL(d.previewUrl);
          } catch (e) {
            // no-op
          }
        }
      });
    };
  }, []);

  const getExpensesTotalAmount = expenseItems =>
    (Array.isArray(expenseItems) ? expenseItems : []).reduce((sum, expense) => {
      const v = parseInt(expense?.cost?.amount, 10);
      return sum + (Number.isFinite(v) ? v : 0);
    }, 0);

  const handleSubmitWithUploads = async values => {
    setUploadError(null);

    if (getExpensesTotalAmount(expenses) <= 0) {
      setUploadError('Add at least one expense with a total cost greater than zero.');
      return;
    }

    const docs = Array.isArray(selectedDocuments) ? selectedDocuments : [];
    if (docs.length === 0) {
      const selectedTransactionId = values?.eventName || null;
      const selectedTx = selectedTransactionId
        ? txItems.find(i => i?.transactionId === selectedTransactionId)
        : null;

      const payload = {
        selectedTransactionId,
        selectedTransaction: selectedTx,
        additionalExpenses: expenses,
        eventUpdate: {
          eventStatus: values?.eventStatus,
          actualEndTime: values?.actualEndTime,
        },
        overtimeDetails: {
          overtimeHours: values?.overtimeHours,
          overtimeRate: values?.overtimeRate,
          totalOvertimeCost: values?.totalOvertimeCost,
          overtimeReason: values?.overtimeReason,
        },
        documents: [],
      };

      return onSubmit ? onSubmit(payload) : undefined;
    }

    try {
      setUploadInProgress(true);

      const uploads = await Promise.all(
        docs.map(d =>
          uploadSingleFileToCloudinary(d.file).catch(err => {
            const name = d?.fileName ? ` (${d.fileName})` : '';
            const message = err?.message || 'File upload failed.';
            throw new Error(`${message}${name}`);
          })
        )
      );

      const selectedTransactionId = values?.eventName || null;
      const selectedTx = selectedTransactionId
        ? txItems.find(i => i?.transactionId === selectedTransactionId)
        : null;

      const payload = {
        selectedTransactionId,
        selectedTransaction: selectedTx,
        additionalExpenses: expenses,
        eventUpdate: {
          eventStatus: values?.eventStatus,
          actualEndTime: values?.actualEndTime,
        },
        overtimeDetails: {
          overtimeHours: values?.overtimeHours,
          overtimeRate: values?.overtimeRate,
          totalOvertimeCost: values?.totalOvertimeCost,
          overtimeReason: values?.overtimeReason,
        },
        documents: uploads.map((u, idx) => ({
          url: u.secure_url,
          publicId: u.public_id,
          fileName: u.original_filename || docs?.[idx]?.fileName,
        })),
      };

      const result = onSubmit ? await onSubmit(payload) : undefined;

      setUploadedDocuments(
        uploads.map((u, idx) => ({
          id: u.public_id || `${Date.now()}-${idx}`,
          url: u.secure_url,
          publicId: u.public_id,
          resourceType: u.resource_type,
          fileName: u.original_filename || docs?.[idx]?.fileName,
          bytes: u.bytes,
          uploadedAt: new Date().toISOString(),
        }))
      );

      setSelectedDocuments(prev => {
        const arr = Array.isArray(prev) ? prev : [];
        arr.forEach(d => {
          if (d && d.previewUrl) {
            try {
              URL.revokeObjectURL(d.previewUrl);
            } catch (e) {
              // no-op
            }
          }
        });
        return [];
      });

      return result;
    } catch (e) {
      setUploadError(e?.message || 'Upload or submission failed.');
      throw e;
    } finally {
      setUploadInProgress(false);
    }
  };

  return (
    <FinalForm
      onSubmit={handleSubmitWithUploads}
      render={({ handleSubmit, form, submitting, pristine, values }) => (
        <form onSubmit={handleSubmit} className={css.form}>
          <div className={css.modalHeader}>
            <h2 className={css.modalTitle}>
              <FormattedMessage id="BillReductionProviderModal.title" defaultMessage="Invoice" />
            </h2>
            <button type="button" className={css.closeButton} onClick={onClose}>
              <IconCollection icon="closeModalIcon" />
            </button>
          </div>

          <div className={css.modalContent}>
            {/* Event Details Section */}
            <div className={css.section}>
              <div className={css.sectionHeader}>
                <span className={css.sectionIcon}>
                  <IconCollection icon="calendarDetailIcon" />
                </span>
                <h3 className={css.sectionTitle}>
                  <FormattedMessage id="BillReductionProviderModal.eventDetails" defaultMessage="Event Details" />
                </h3>
              </div>
              <div className={css.fieldGroup}>
                <FieldSelect
                  id="eventName"
                  name="eventName"
                  label={intl.formatMessage({
                    id: 'BillReductionProviderModal.eventNameLabel',
                    defaultMessage: 'Official Event Name',
                  })}
                  className={css.selectField}
                  disabled={Boolean(isLoading)}
                >
                  <option value="">
                    {intl.formatMessage({
                      id: 'BillReductionProviderModal.selectEventName',
                      defaultMessage: 'Select event name',
                    })}
                  </option>
                  {isLoading ? (
                    <option value="" disabled>
                      Loading events…
                    </option>
                  ) : null}
                  {!isLoading && txItems.length
                    ? txItems.map(item => {
                      const txId = item?.transactionId;
                      const listingTitle = item?.listing?.title || item?.listing?.publicData?.eventTitle;
                      const label = listingTitle || txId;

                      const eventDates = item?.listing?.publicData?.eventDates;
                      const latestEventDate = getLatestEventDate(eventDates);
                      {/* const isInFuture = latestEventDate ? latestEventDate.getTime() > Date.now() : false; */ } // TODO: Enable this
                      const isInFuture = false

                      return (
                        <option key={txId} value={txId} disabled={isInFuture}>
                          {label}
                        </option>
                      );
                    })
                    : null}
                </FieldSelect>
                {isLoading ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                    <IconSpinner />
                    <span>
                      {intl.formatMessage({
                        id: 'BillReductionProviderModal.loading',
                        defaultMessage: 'Loading…',
                      })}
                    </span>
                  </div>
                ) : null}
              </div>
            </div>

            {/* Additional Expenses Section */}
            <div className={css.section}>
              <div className={css.sectionHeader}>
                <span className={css.sectionIcon}>
                  <IconCollection icon="documentIcon" />
                </span>
                <h3 className={css.sectionTitle}>
                  <FormattedMessage id="BillReductionProviderModal.additionalExpenses" defaultMessage="Additional Expenses" />
                </h3>
              </div>
              <div className={css.expenseRow}>
                <div className={css.expenseField}>
                  <FieldTextInput
                    id="expenseDraft-category"
                    name="expenseDraft.category"
                    type="text"
                    label={intl.formatMessage({
                      id: 'BillReductionProviderModal.serviceCategoryLabel',
                      defaultMessage: 'Service Category',
                    })}
                    placeholder={intl.formatMessage({
                      id: 'BillReductionProviderModal.serviceCategoryPlaceholder',
                      defaultMessage: 'Enter service category',
                    })}
                    className={css.field}
                  />
                </div>
                <div className={css.quantityField}>
                  <FieldTextInput
                    id="expenseDraft-quantity"
                    name="expenseDraft.quantity"
                    type="number"
                    label={intl.formatMessage({
                      id: 'BillReductionProviderModal.quantityLabel',
                      defaultMessage: 'Quantity',
                    })}
                    placeholder={intl.formatMessage({
                      id: 'BillReductionProviderModal.quantityPlaceholder',
                      defaultMessage: 'Enter quantity',
                    })}
                    className={css.field}
                  />
                </div>
                <div className={css.costField}>
                  <FieldCurrencyInput
                    id="expenseDraft-cost"
                    name="expenseDraft.cost"
                    key={`expenseDraft-cost-${expenses.length}`}
                    label={intl.formatMessage({
                      id: 'BillReductionProviderModal.totalCostLabel',
                      defaultMessage: 'Total Cost',
                    })}
                    placeholder={intl.formatMessage({
                      id: 'BillReductionProviderModal.costPlaceholder',
                      defaultMessage: 'Enter cost',
                    })}
                    currencyConfig={config.currencyConfig}
                    className={css.currencyField}
                  />
                </div>
              </div>

              <button
                type="button"
                className={css.addDeductionButton}
                onClick={() => handleAddExpense(values, form)}
                disabled={(() => {
                  if (expenses.length >= 3) return true;
                  const draft = values?.expenseDraft || {};
                  const category = (draft?.category || '').trim();
                  const quantity = draft?.quantity;
                  const cost = draft?.cost;
                  const hasQty = quantity !== undefined && quantity !== null && quantity !== '';
                  const hasCost = cost !== undefined && cost !== null && cost !== '';
                  return !(category && hasQty && hasCost);
                })()}
              >
                <span className={css.addIcon}>+</span>
                <FormattedMessage id="BillReductionProviderModal.addExpense" defaultMessage="Add Expense" />
              </button>

              {Array.isArray(expenses) && expenses.length ? (
                <div className={css.uploadedFiles}>
                  {expenses.map(exp => {
                    const category = exp?.category;
                    const quantity = exp?.quantity;
                    const cost = exp?.cost;
                    const moneyObj = new Money(cost?.amount, cost?.currency);
                    const formatCost = formatMoney(intl, moneyObj);

                    return (
                    <div key={exp.id} className={css.uploadedFile}>
                      <div className={css.fileInfo}>
                        <div className={css.fileDetails}>
                          <span className={css.fileName}>{category}</span>
                          <span className={css.fileSize}>
                            {`Qty: ${quantity} • Cost: ${formatCost}`}
                          </span>
                        </div>
                      </div>
                      <button
                        type="button"
                        className={css.removeFileButton}
                        onClick={() => handleRemoveExpense(exp.id)}
                        aria-label="Remove expense"
                      >
                        <IconCollection icon="trashDeleteIcon" />
                      </button>
                    </div>
                  )
                  })}
                </div>
              ) : null}
            </div>

            {/* Event Update Section */}
            <div className={css.section}>
              <div className={css.sectionHeader}>
                <span className={css.sectionIcon}>
                  <IconCollection icon="checkCircleIcon" />
                </span>
                <h3 className={css.sectionTitle}>
                  <FormattedMessage id="BillReductionProviderModal.eventUpdate" defaultMessage="Event Update" />
                </h3>
              </div>
              <div className={css.eventUpdateRow}>
                <div className={css.eventUpdateField}>
                  <FieldSelect
                    id="eventStatus"
                    name="eventStatus"
                    label={intl.formatMessage({
                      id: 'BillReductionProviderModal.eventStatusLabel',
                      defaultMessage: 'Event Status',
                    })}
                    className={css.selectField}
                  >
                    <option value="">
                      {intl.formatMessage({
                        id: 'BillReductionProviderModal.selectTime',
                        defaultMessage: 'Select time',
                      })}
                    </option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="in_progress">In Progress</option>
                  </FieldSelect>
                </div>
                <div className={css.eventUpdateField}>
                  <FieldTextInput
                    id="actualEndTime"
                    name="actualEndTime"
                    type="time"
                    label={intl.formatMessage({
                      id: 'BillReductionProviderModal.actualEndTimeLabel',
                      defaultMessage: 'Actual End Time',
                    })}
                    className={css.field}
                  />
                </div>
              </div>
            </div>

            {/* Important Notice Section */}
            <div className={css.section}>
              <div className={css.importantNotice}>
                <div className={css.noticeIcon}>
                  <IconCollection icon="warningCircleIcon" />
                </div>
                <div className={css.noticeContent}>
                  <h4 className={css.noticeTitle}>
                    <FormattedMessage id="BillReductionProviderModal.importantNotice" defaultMessage="Important Notice" />
                  </h4>
                  <p className={css.noticeText}>
                    <FormattedMessage
                      id="BillReductionProviderModal.noticeMessage"
                      defaultMessage="Once you mark an event as completed, you cannot change the status back. Please ensure all event activities are finished and all documentation is complete before updating the status."
                    />
                  </p>
                </div>
              </div>
            </div>

            {/* Supporting Document Section */}
            <div className={css.section}>
              <div className={css.sectionHeader}>
                <span className={css.sectionIcon}>
                  <IconCollection icon="documentIcon" />
                </span>
                <h3 className={css.sectionTitle}>
                  <FormattedMessage id="BillReductionProviderModal.supportingDocument" defaultMessage="Supporting Document" />
                </h3>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                style={{ display: 'none' }}
                onChange={handleDocumentSelected}
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
              />
              <div className={css.uploadContainer} onClick={handlePickDocument}>
                <div>
                  <IconCollection icon="documentUploadIcon" />
                </div>
                <span className={css.uploadHeading}>
                  <FormattedMessage
                    id="BillReductionProviderModal.uploadDocuments"
                    defaultMessage="Upload Supporting Documents"
                  />
                </span>
                <div className={css.uploadSubHeading}>
                  <FormattedMessage
                    id="BillReductionProviderModal.uploadDescription"
                    defaultMessage="Photos, receipts, client communications, or any other relevant files"
                  />
                </div>
                <p className={css.uploadHint}>
                  <FormattedMessage
                    id="BillReductionProviderModal.supportedFormats"
                    defaultMessage="Supported formats: PDF, JPG, PNG, DOC (Max 10MB each)"
                  />
                </p>
                {uploadInProgress ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
                    <IconSpinner />
                    <span>
                      <FormattedMessage
                        id="BillReductionProviderModal.uploading"
                        defaultMessage="Uploading…"
                      />
                    </span>
                  </div>
                ) : null}
              </div>
              {uploadError ? <div className={css.errorText}>{uploadError}</div> : null}
              {(() => {
                const hasUploaded = Array.isArray(uploadedDocuments) && uploadedDocuments.length > 0;
                const hasSelected = Array.isArray(selectedDocuments) && selectedDocuments.length > 0;
                const docsToShow = hasUploaded ? uploadedDocuments : selectedDocuments;
                if (!docsToShow || docsToShow.length === 0) {
                  return null;
                }

                return (
                  <div className={css.uploadedFiles}>
                    {docsToShow.map(doc => {
                      const name = doc?.fileName || doc?.original_filename || 'File';
                      const sizeBytes = typeof doc?.bytes === 'number' ? doc.bytes : null;
                      const sizeMb = sizeBytes ? (sizeBytes / (1024 * 1024)).toFixed(2) : null;
                      const isPdf = String(name).toLowerCase().endsWith('.pdf');
                      const icon = isPdf ? 'filePdfIcon' : 'fileImageIcon';

                      return (
                        <div key={doc?.id || name} className={css.uploadedFile}>
                          <div className={css.fileInfo}>
                            <div className={css.fileIcon}>
                              {!hasUploaded && doc?.previewUrl ? (
                                <img
                                  src={doc.previewUrl}
                                  alt={name}
                                  style={{ width: 32, height: 32, objectFit: 'cover' }}
                                />
                              ) : (
                                <IconCollection icon={icon} />
                              )}
                            </div>
                            <div className={css.fileDetails}>
                              <span className={css.fileName}>{name}</span>
                              <span className={css.fileSize}>{sizeMb ? `${sizeMb} MB` : ''}</span>
                            </div>
                          </div>
                          {!hasUploaded ? (
                            <button
                              type="button"
                              className={css.removeFileButton}
                              onClick={() => handleRemoveDocument(doc.id)}
                              aria-label="Remove file"
                            >
                              <IconCollection icon="trashDeleteIcon" />
                            </button>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Modal Footer */}
          <div className={css.modalFooter}>
            {/* <Button
              type="button"
              className={css.saveDraftButton}
              onClick={() => {
                // Handle save draft
              }}
            >
              <FormattedMessage id="BillReductionProviderModal.saveDraft" defaultMessage="Save Draft" />
            </Button> */}
            <Button
              type="submit"
              className={css.submitButton}
              inProgress={submitting}
              disabled={(() => {
                if (submitting) return true;

                // Expense Request: Additional Expenses are required, Overtime
                // Details are optional, and the Event Update section
                // (eventStatus / actualEndTime) is always optional.
                const selectedEvent = values?.eventName;

                const hasRequiredFields = !!selectedEvent;

                const hasPositiveExpenseTotal = getExpensesTotalAmount(expenses) > 0;

                const hasDoc =
                  (Array.isArray(selectedDocuments) && selectedDocuments.length > 0) ||
                  (Array.isArray(uploadedDocuments) && uploadedDocuments.length > 0);
                const notUploading = !uploadInProgress;

                return !(hasRequiredFields && hasPositiveExpenseTotal && hasDoc && notUploading);
              })()}
            >
              <FormattedMessage id="CreateInvoiceProviderModal.submitRequest" defaultMessage="Create Invoice" />
            </Button>
          </div>
        </form>
      )}
    />
  );
};

export default CreateInvoiceProviderModal;